import { guideMemory } from './guide-memory'
import { getMe } from './me'
import { el, todayISO } from './ui'

// Design E (Liam, 3 Nov 2026: "maybe lose the extra text ... the first time used there could be an animation to show
// instead of this text", then "E ship it"; https://claude.ai/artifact/6MZeS9Zw81TBB1G9RSAxpk): the phone Week's Due
// tray no longer says "Hold and drag a name onto a day". The first time Due opens on this phone, for this person and
// round, a see-through copy of the first name lifts and glides up onto a day, twice, then never again. A tap stops it.
// Nothing is booked or moved. With reduced motion, or with nobody signed in, the words stay instead.

// The phone's Week and the computer's Due panel each show it once (Liam picked G for the computer, 3 Nov 2026).
type Where = 'phone' | 'desk'
const STEP = { phone: 'diary-due-drag-demo', desk: 'diary-due-drag-demo-desk' }
const played = { phone: false, desk: false }
const CARD = { phone: '.diary-week-due-card', desk: '.diary-due-col-card[data-pick]' }
const NAME = { phone: '.diary-week-name', desk: '.sched-clean-link, strong' }
const DAYS = { phone: '#screen.diary-phone-week-view .diary-week-strip button[data-date]', desk: '.diary-desk-strip [data-date]' }

function memory(): ReturnType<typeof guideMemory> | null {
  const me = getMe()
  if (!me?.businessId || !me.email) return null
  return guideMemory(`${me.businessId}:${me.email}`, () => localStorage)
}
const reduced = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

// 'demo': play it; 'text': keep the words; 'none': learnt already.
export function dueDragTeach(where: Where): 'demo' | 'text' | 'none' {
  const mem = memory()
  if (!mem || reduced()) return 'text'
  if (played[where] || mem.seen(STEP[where])) return 'none'
  return 'demo'
}

export function playDueDragDemo(body: HTMLElement, where: Where): void {
  const mem = memory()
  if (!mem || played[where] || mem.seen(STEP[where])) return
  played[where] = true
  requestAnimationFrame(() => {
    const card = body.querySelector<HTMLElement>(CARD[where])
    const tile = [...document.querySelectorAll<HTMLElement>(DAYS[where])].find(t => (t.dataset.date ?? '') >= todayISO())
    const name = card?.querySelector<HTMLElement>(NAME[where])?.textContent?.trim()
    // Nothing to show yet (still loading, or nobody due): try again on the next drawing.
    if (!card || !tile || !name) { played[where] = false; return }
    const from = card.getBoundingClientRect(), to = tile.getBoundingClientRect()
    const ghost = el('div', { class: 'diary-week-ghost due-drag-demo', 'aria-hidden': 'true' }, name)
    if (typeof ghost.animate !== 'function') { played[where] = false; return }
    mem.complete(STEP[where])
    ghost.style.left = `${Math.round(from.left + 12)}px`; ghost.style.top = `${Math.round(from.top + 4)}px`; ghost.style.animation = 'none'
    document.body.append(ghost)
    const dx = to.left + to.width / 2 - (from.left + 12 + 90), dy = to.top + to.height / 2 - (from.top + 4 + 22)
    const lift = 'scale(1.04) rotate(-2deg)'
    const run = ghost.animate([
      { opacity: 0, transform: 'none', offset: 0 },
      { opacity: .95, transform: lift, offset: .12 },
      { opacity: .95, transform: `translate(${dx}px, ${dy}px) ${lift}`, offset: .6 },
      { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(.9)`, offset: .78 },
      { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(.9)`, offset: 1 },
    ], { duration: 2000, iterations: 2, easing: 'cubic-bezier(.2,.8,.2,1)' })
    const lit: number[] = []
    for (const at of [1050, 3050]) {
      lit.push(window.setTimeout(() => tile.classList.add('diary-week-drop-over'), at))
      lit.push(window.setTimeout(() => tile.classList.remove('diary-week-drop-over'), at + 450))
    }
    card.classList.add('due-drag-demo-from')
    const stop = (): void => {
      run.cancel(); ghost.remove(); card.classList.remove('due-drag-demo-from')
      for (const t of lit) window.clearTimeout(t)
      tile.classList.remove('diary-week-drop-over')
      window.removeEventListener('pointerdown', stop, true); window.removeEventListener('hashchange', stop)
    }
    run.onfinish = stop
    window.addEventListener('pointerdown', stop, true); window.addEventListener('hashchange', stop)
  })
}
